/**
 * Normalizadores wire → *Local para los endpoints `/tenants/:tid/*` del rbac.
 *
 * El backend devuelve 200 con un shape que NO coincide con el que los componentes asumían
 * (campos ausentes, nombres distintos, estructura flat vs anidada). Acceder a esos campos
 * `undefined` crasheaba el render → ErrorBoundary global. Estos mappers son el único punto
 * donde el shape real se traduce al modelo que consumen los componentes, SIEMPRE con defaults
 * (nunca dejan un campo requerido en `undefined`).
 *
 * Se aplican dentro de `tenantApi.*` (api/endpoints/tenant.ts). Los tests que mockean
 * `tenantApi.*` devuelven directamente el *Local ya normalizado, así que no se ven afectados.
 */
import type {
    TenantUserListItemLocal,
    TenantUserListResponseLocal,
    TenantUserDetailLocal,
    TenantUserStatusLocal,
    TenantRoleListItemLocal,
    TenantRoleListResponseLocal,
    TenantBrandingLocal,
    AuditLogEntryLocal,
    AuditQueryResponseLocal,
    UserLevelScopeLocal,
} from './TenantLocal';
import type { Permission, Scope } from './permissions';
import type { RoleAssignmentInfoLocal } from './MeLocal';
import { isUuid } from '../../lib/displayName';
import type {
    SharedUserRowWire,
    TenantUserListWire,
    TenantUserDetailWire,
    RoleAssignmentInfoWire,
    SharedRoleRowWire,
    TenantRoleListWire,
    BrandingConfigWire,
    SharedAuditLogRowWire,
    AuditQueryWire,
} from './TenantWire';

// El front ahora usa los MISMOS estados que el backend (SharedUserStatus) → passthrough directo.
// DEC-RBAC-091 (Fase 5): removidos RETAILER/STORE/GROUP. Los scopes válidos son PLATFORM/TENANT +
// niveles genéricos LEVEL_1/2/3 (sin ellos normalizeScope tiraba a undefined el scope LEVEL_N de un rol).
export const KNOWN_SCOPES: Scope[] = ['PLATFORM', 'TENANT', 'LEVEL_1', 'LEVEL_2', 'LEVEL_3'];

/** Scope del backend (`SharedRoleRow.scope`) → `Scope` tipado. `undefined` si no viene o es inválido. */
export function normalizeScope(scope: string | undefined): Scope | undefined {
    return scope && (KNOWN_SCOPES as string[]).includes(scope) ? (scope as Scope) : undefined;
}

const KNOWN_STATUSES: TenantUserStatusLocal[] = [
    'INVITED',
    'ACTIVE',
    'SUSPENDED',
    'LOCKED',
    'OFFBOARDED',
    'DELETED',
];

/** Status del backend (`SharedUserStatus`) → front (1:1). Fallback defensivo a ACTIVE si llega algo raro. */
function normalizeUserStatus(status: string | undefined): TenantUserStatusLocal {
    if (status && (KNOWN_STATUSES as string[]).includes(status)) {
        return status as TenantUserStatusLocal;
    }
    return 'ACTIVE';
}

/** epoch ms (number) | ISO (string) | nulo → ISO string (o '' si no hay dato). */
function toIso(value: number | string | null | undefined): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'number') return new Date(value).toISOString();
    return value;
}

/* ─── Users ─── */

/**
 * `levelScopes` del wire → `UserLevelScopeLocal[]`. Descarta las entradas sin `scopeRef` o con un
 * `level` fuera de `KNOWN_SCOPES` (mismo criterio que el resto de los normalizers: la UI nunca
 * hidrata un scope inventado). `displayName` NO cae al `scopeRef`: ausente se queda ausente, y la
 * celda decide cómo mostrarlo. Default `[]` — el backend puede no mandarlo (back-compat).
 */
function normalizeLevelScopes(raw: SharedUserRowWire['levelScopes']): UserLevelScopeLocal[] {
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((entry) => {
        const level = normalizeScope(entry.level);
        if (!level || !entry.scopeRef) return [];
        return [{ level, scopeRef: entry.scopeRef, displayName: entry.displayName }];
    });
}

export function normalizeTenantUser(row: SharedUserRowWire): TenantUserListItemLocal {
    return {
        cognitoSub: row.cognitoSub ?? '',
        email: row.email ?? '',
        // DEC-RBAC-079/081: teléfono en la fila del list (para mostrar el identificador principal del
        // tenant). Passthrough defensivo; puede no venir.
        phone: row.phone,
        displayName: row.displayName ?? '',
        status: normalizeUserStatus(row.status),
        // El backend NO denormaliza roles en la fila del user (viven en SharedRoleAssignment).
        // Hasta que exista un endpoint que los traiga, lista vacía → la UI muestra "Sin roles".
        roles: Array.isArray(row.roles) ? row.roles : [],
        // El list enriquece la fila con el scope de mayor jerarquía (assignment top). Passthrough.
        topScope: row.topScope,
        topScopeRef: row.topScopeRef,
        // TODAS las entidades de nivel del user (columnas por nivel + filtro por grupo).
        levelScopes: normalizeLevelScopes(row.levelScopes),
        createdAt: toIso(row.createdAt),
        // Campos de usuario de API: solo aparecen si vienen, así la forma de una persona no cambia.
        ...(row.accountType === 'API' ? { accountType: 'API' as const } : {}),
        ...(row.technicalContact ? { technicalContact: row.technicalContact } : {}),
        ...(Array.isArray(row.ipAllowlist) ? { ipAllowlist: row.ipAllowlist } : {}),
    };
}

export function normalizeTenantUserList(
    wire: TenantUserListWire | null | undefined,
): TenantUserListResponseLocal {
    const w = wire ?? {};
    return {
        items: (w.items ?? []).map(normalizeTenantUser),
        lastEvaluatedKey: w.lastEvaluatedKey ?? w.nextKey,
    };
}

function normalizeRoleAssignment(a: RoleAssignmentInfoWire): RoleAssignmentInfoLocal {
    return {
        assignmentId: a.assignmentId ?? '',
        roleId: a.roleId ?? '',
        // DEC-013: `roleName` cae a undefined si el backend mandó el roleId crudo como nombre, así el
        // consumidor (resolver use*NameById) puede resolver el nombre real en vez de pintar el UUID.
        roleName: a.roleName && !isUuid(a.roleName) ? a.roleName : (a.roleId ?? ''),
        scope: (a.scope ?? 'TENANT') as RoleAssignmentInfoLocal['scope'],
        scopeRef: a.scopeRef ?? '*',
        // DEC-013: el backend rellena `scopeRefName` con el `scopeRef` crudo (UUID) cuando no tiene el
        // nombre real. NO lo propagamos como si fuera un nombre: si tiene forma de UUID (o == scopeRef),
        // lo dejamos en `undefined` para que el consumidor caiga a su propio resolver/`displayNameOrShort`
        // y NUNCA muestre el UUID pelado. El campo es opcional justamente para esto.
        scopeRefName:
            a.scopeRefName && a.scopeRefName !== a.scopeRef && !isUuid(a.scopeRefName)
                ? a.scopeRefName
                : undefined,
        scopeRefParentName:
            a.scopeRefParentName && !isUuid(a.scopeRefParentName)
                ? a.scopeRefParentName
                : undefined,
        grantedAt: toIso(a.grantedAt),
    };
}

export function normalizeTenantUserDetail(
    wire: TenantUserDetailWire | null | undefined,
): TenantUserDetailLocal {
    const w = wire ?? {};
    return {
        ...normalizeTenantUser(w),
        phone: w.phone,
        mfaConfigured: w.mfaConfigured ?? false,
        roleAssignments: (w.roleAssignments ?? []).map(normalizeRoleAssignment),
        // Valores de campos personalizados para precargar el form de edición. Passthrough
        // defensivo: si el backend no los manda, queda undefined (el form cae a '' por campo).
        customFields: w.customFields,
    };
}

/* ─── Roles ─── */

export function normalizeTenantRole(row: SharedRoleRowWire): TenantRoleListItemLocal {
    return {
        roleId: row.roleId ?? '',
        // El backend usa `displayName`; el front espera `roleName`. Sin él la fila quedaba en blanco.
        roleName: row.roleName ?? row.displayName ?? row.roleId ?? '',
        description: row.description,
        isSystem: row.isSystem ?? false,
        permissions: (row.permissions ?? []) as Permission[],
        // Nivel REAL del rol (fuente de verdad del scope al crear/asignar). undefined si no viene.
        scope: normalizeScope(row.scope),
    };
}

export function normalizeTenantRoleList(
    wire: TenantRoleListWire | null | undefined,
): TenantRoleListResponseLocal {
    return { items: ((wire ?? {}).items ?? []).map(normalizeTenantRole) };
}

/* ─── Branding ─── */

export function normalizeBranding(
    wire: BrandingConfigWire | null | undefined,
): TenantBrandingLocal {
    const w = wire ?? {};
    return {
        primaryColor: w.primaryColor ?? '',
        secondaryColor: w.secondaryColor ?? '',
        accentColor: w.accentColor ?? '',
        logoUrl: w.logoUrl ?? '',
        logoWidth: w.logoWidth,
        faviconUrl: w.faviconUrl,
        loginUrl: w.loginUrl,
    };
}

/* ─── Audit ─── */

export function normalizeAuditEntry(row: SharedAuditLogRowWire): AuditLogEntryLocal {
    return {
        auditId: row.auditId ?? row.changeId ?? '',
        timestamp: toIso(row.timestamp),
        actor: {
            cognitoSub: row.actorCognitoSub ?? '',
            // El backend no devuelve el email del actor en la fila de auditoría (solo el sub).
            email: '',
            // displayName resuelto server-side (lo puebla el audit del retailer). Ausente en tenant.
            displayName: row.actorDisplayName,
        },
        action: row.action ?? row.changeType ?? '',
        target: {
            type: row.entityType ?? '',
            id: row.entityId ?? '',
        },
        before: row.before,
        after: row.after,
    };
}

export function normalizeAuditQuery(
    wire: AuditQueryWire | null | undefined,
): AuditQueryResponseLocal {
    const w = wire ?? {};
    return {
        items: (w.items ?? []).map(normalizeAuditEntry),
        lastEvaluatedKey: w.lastEvaluatedKey ?? w.nextKey,
    };
}
